// The globe: where the stories happen, drawn like an engraving in a broadsheet. Canvas 2D,
// orthographic, the night side shaded from where the sun is now. Every colour is a token, read
// at runtime and read again when the theme changes. It draws only while something moves.

import { useEffect, useRef } from "react";
import { geoCircle, geoGraticule10, geoOrthographic, geoPath, type GeoPermissibleObjects } from "d3-geo";
import { feature, mesh } from "topojson-client";
import { usePref } from "../state/prefs";
import type { GlobePoint, GlobeProps } from "./types";
import "./globe.css";

// ---------- constants ----------

const RAD = Math.PI / 180;
const TAU = Math.PI * 2;
/** Mark radius, px: a sqrt scale of weight. */
const R_MIN = 2.5;
const R_MAX = 14;
/** Room around the sphere for the focus ring. */
const PAD = 8;
const PITCH_MAX = 60;
/** Idle spin, degrees per ms (4°/s, eastward). */
const SPIN = 0.004;
/** The spin waits this long after any touch, key or drag. */
const IDLE_MS = 4000;
const CENTRE_MS = 900;
const KEY_MS = 240;
const PULSE_EVERY = 3000;
const PULSE_MS = 1200;
/** A quake's waves leave the selected point this far apart, ms. */
const WAVE_GAP = 260;
/** Waves per cycle: one for the faintest quake, up to this many for the strongest. */
const WAVES_MAX = 4;
/** How far a wave runs over the surface, degrees of arc: the faintest quake to the strongest. */
const REACH_MIN = 6;
const REACH_MAX = 60;
/** How long one wave runs, ms: the faintest quake to the strongest. */
const WAVE_MS_MIN = 1400;
const WAVE_MS_MAX = 2600;
/** A wave starts as a circle this wide, degrees. */
const WAVE_FROM = 0.5;
/** The felt area, shaded under the marks: this share of the waves' reach. */
const FELT = 0.35;
const HOVER_PX = 12;
const TAP_PX = 20;
/** Inertia decay time constant, ms: a flick coasts about velocity × this far. */
const COAST_MS = 200;
/** Depth (cosine of the distance from the view centre) over which marks fade and shrink at the limb. */
const LIMB = 0.2;
/** Marks this close to the limb cannot be hovered or picked. */
const PICK_DEPTH = 0.06;
const TONE = { up: 0, down: 1, news: 2, warn: 3 } as const;
const SPHERE: GeoPermissibleObjects = { type: "Sphere" };

// ---------- the atlas, loaded once, on first use ----------

type Topology = Parameters<typeof mesh>[0];
type MeshObject = NonNullable<Parameters<typeof mesh>[1]>;
type Atlas = { land: GeoPermissibleObjects; coast: GeoPermissibleObjects; borders: GeoPermissibleObjects };

let atlas: Atlas | null = null;
let atlasLoading: Promise<Atlas> | null = null;

function loadAtlas(): Promise<Atlas> {
  atlasLoading ??= import("world-atlas/countries-110m.json").then(
    (module) => {
      const topology = module.default as unknown as Topology;
      const land = topology.objects["land"];
      const countries = topology.objects["countries"];
      if (!land || !countries) throw new Error("world-atlas: land or countries is missing");
      atlas = {
        land: feature(topology, land),
        coast: mesh(topology, land as MeshObject),
        borders: mesh(topology, countries as MeshObject, (a, b) => a !== b),
      };
      return atlas;
    },
    (error: unknown) => {
      atlasLoading = null;
      throw error;
    },
  );
  return atlasLoading;
}

let graticule: GeoPermissibleObjects | null = null;

// ---------- the sun ----------

const wrap180 = (degrees: number) => ((((degrees + 180) % 360) + 360) % 360) - 180;

/** Where the sun is overhead at `time` (ms), as [lon, lat] degrees: NOAA's series, within a degree. */
function subsolar(time: number): [number, number] {
  const date = new Date(time);
  const days = (time - Date.UTC(date.getUTCFullYear(), 0, 1)) / 86_400_000;
  const g = ((2 * Math.PI) / 365) * (days - 0.5);
  const eqTime =
    229.18 *
    (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const declination =
    0.006918 -
    0.399912 * Math.cos(g) +
    0.070257 * Math.sin(g) -
    0.006758 * Math.cos(2 * g) +
    0.000907 * Math.sin(2 * g) -
    0.002697 * Math.cos(3 * g) +
    0.00148 * Math.sin(3 * g);
  const minutes = (((time % 86_400_000) + 86_400_000) % 86_400_000) / 60_000;
  return [wrap180((720 - minutes - eqTime) / 4), declination / RAD];
}

// ---------- colours ----------

type Palette = {
  surface: string;
  fill: string;
  line: string;
  lineStrong: string;
  ink: string;
  ink2: string;
  focus: string;
  tones: readonly [string, string, string, string];
  /** Ink is light: the night edition. */
  dark: boolean;
};

/** Relative lightness of a `#rgb`, `#rrggbb` or `rgb()` colour, 0 to 1; null when unreadable. */
function lightness(colour: string): number | null {
  let r: number, g: number, b: number;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(colour);
  if (hex?.[1]) {
    const digits = hex[1].length === 3 ? hex[1].replace(/./g, "$&$&") : hex[1];
    const value = parseInt(digits, 16);
    r = (value >> 16) & 255;
    g = (value >> 8) & 255;
    b = value & 255;
  } else {
    const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(colour);
    if (!rgb) return null;
    r = Number(rgb[1]);
    g = Number(rgb[2]);
    b = Number(rgb[3]);
  }
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function readPalette(element: Element): Palette {
  const css = getComputedStyle(element);
  const read = (name: string) => css.getPropertyValue(name).trim();
  const surface = read("--c-surface");
  const ink = read("--c-ink");
  const surfaceLight = lightness(surface);
  const inkLight = lightness(ink);
  const dark =
    surfaceLight !== null && inkLight !== null ? inkLight > surfaceLight : css.colorScheme.includes("dark");
  return {
    surface,
    fill: read("--c-fill"),
    line: read("--c-line"),
    lineStrong: read("--c-line-strong"),
    ink,
    ink2: read("--c-ink-2"),
    focus: read("--c-focus"),
    tones: [read("--c-up"), read("--c-down"), read("--c-ink-2"), read("--c-warn")],
    dark,
  };
}

// ---------- the engine ----------

type Mark = {
  point: GlobePoint;
  weight: number;
  /** Longitude, radians. */
  lambda: number;
  cosPhi: number;
  sinPhi: number;
  r: number;
  tone: 0 | 1 | 2 | 3;
  /** Projected, css px. */
  x: number;
  y: number;
  /** Cosine of the distance from the view centre: > 0 on the near side. */
  depth: number;
};

type Parts = {
  wrap: HTMLDivElement;
  canvas: HTMLCanvasElement;
  tip: HTMLDivElement;
  tipLabel: HTMLDivElement;
  tipSub: HTMLDivElement;
  live: HTMLDivElement;
  pick: (id: string) => void;
};

type Engine = {
  setPoints(points: readonly GlobePoint[]): void;
  setSelected(id: string | null): void;
  setSpin(on: boolean): void;
  setQuake(q: number): void;
  retheme(): void;
  destroy(): void;
};

const clampPitch = (degrees: number) => Math.max(-PITCH_MAX, Math.min(PITCH_MAX, degrees));
/** A flick coasts at most this fast, degrees per ms (so at most 50° on). */
const clampVelocity = (velocity: number) => Math.max(-0.25, Math.min(0.25, velocity));
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const easeOut = (t: number) => 1 - (1 - t) ** 3;

function createEngine(parts: Parts): Engine | null {
  const { wrap, canvas, tip, tipLabel, tipSub, live } = parts;
  const context = canvas.getContext("2d");
  if (!context) return null;
  const ctx = context;

  const projection = geoOrthographic().clipAngle(90).precision(0.5);
  const path = geoPath(projection, ctx);
  /** [yaw, pitch] in degrees: the view centre is (-yaw, -pitch). */
  const rotation: [number, number] = [-15, -24];
  const translate: [number, number] = [0, 0];
  const grid = (graticule ??= geoGraticule10());
  const circle = geoCircle().radius(90).precision(2);
  /** The quake's felt area and waves, centred on the selected point. */
  const quakeCircle = geoCircle().precision(3);
  const born = performance.now();

  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const schemeQuery = window.matchMedia("(prefers-color-scheme: dark)");

  // size, css px
  let width = 0;
  let height = 0;
  let dpr = 1;
  let radius = 0;

  let palette = readPalette(wrap);
  let paletteStale = false;
  let night: GeoPermissibleObjects = SPHERE;
  let day: GeoPermissibleObjects = SPHERE;

  let marks: Mark[] = [];
  let byId = new Map<string, Mark>();
  let selectedId: string | null = null;
  let selected: Mark | null = null;
  let centredId: string | null = null;
  let hovered: Mark | null = null;
  let tapped: Mark | null = null;
  let centreMark: Mark | null = null;
  let tipMark: Mark | null = null;
  /** The note's words or size may have changed: fill and measure it again. */
  let tipStale = false;
  let tipW = 0;
  let tipH = 0;
  let announced: Mark | null = null;

  let spinOn = false;
  let reduced = motionQuery.matches;
  let pageVisible = !document.hidden;
  let inView = true;
  let keyboard = false;
  let lastInteraction = Number.NEGATIVE_INFINITY;
  let pulseFrom = 0;
  // the selected point's quake, 0 for none, and what it sends out: waves a cycle, their reach (degrees) and run (ms)
  let quake = 0;
  let waves = 1;
  let reach = REACH_MIN;
  let waveMs = WAVE_MS_MIN;
  let cursor = "";

  // drag
  let pointerId: number | null = null;
  let pointerKind = "mouse";
  /** Touch decides between turning the globe and scrolling the page on its first few px. */
  let lock: "pending" | "turn" | "scroll" = "pending";
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let lastY = 0;
  let lastMove = 0;
  let travel = 0;
  let dragging = false;
  let velYaw = 0;
  let velPitch = 0;
  let coasting = false;

  // hover
  let pointerInside = false;
  let hoverX = 0;
  let hoverY = 0;

  // a turn: to a story, or a key's step
  let animating = false;
  let animFrom0 = 0;
  let animFrom1 = 0;
  let animTo0 = 0;
  let animTo1 = 0;
  let animStart = 0;
  let animMs = 0;
  let animEase = easeInOut;

  // loop
  let raf = 0;
  let lastFrame = -1;
  let wakeTimer = 0;
  let wakeAt = 0;
  let destroyed = false;
  /** Set by a draw that changed what it shows (a mark turned under the pointer): draw once more. */
  let again = false;

  function sunNow() {
    const [lon, lat] = subsolar(Date.now());
    night = circle.center([wrap180(lon + 180), -lat])();
    day = circle.center([lon, lat])();
  }
  sunNow();

  // ---------- scheduling ----------

  function invalidate() {
    if (raf || destroyed || !pageVisible || !inView || width === 0) return;
    raf = requestAnimationFrame(frame);
  }

  function stop() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    lastFrame = -1;
    if (wakeTimer) clearTimeout(wakeTimer);
    wakeTimer = 0;
    wakeAt = 0;
  }

  /** Draw again at `at` (performance time) without spinning frames until then. */
  function wake(at: number) {
    if (wakeTimer && wakeAt <= at) return;
    if (wakeTimer) clearTimeout(wakeTimer);
    wakeAt = at;
    wakeTimer = window.setTimeout(
      () => {
        wakeTimer = 0;
        wakeAt = 0;
        invalidate();
      },
      Math.max(0, at - performance.now()),
    );
  }

  function frame(now: number) {
    raf = 0;
    // the idle spin needs no more than about 60 frames a second, on a 120 Hz screen too
    if (lastFrame >= 0 && now - lastFrame < 12 && !animating && !coasting && pointerId === null) {
      raf = requestAnimationFrame(frame);
      return;
    }
    const dt = lastFrame < 0 ? 16.7 : Math.min(64, now - lastFrame);
    lastFrame = now;
    const moving = advance(now, dt);
    again = false;
    draw(now);
    const pulsing = pulse(now);
    if ((moving || pulsing || again) && pageVisible && inView) raf = requestAnimationFrame(frame);
    else lastFrame = -1;
  }

  /** Moves the rotation one frame on; true while it keeps moving. */
  function advance(now: number, dt: number): boolean {
    if (animating) {
      const t = Math.min(1, (now - animStart) / animMs);
      const e = animEase(t);
      rotation[0] = wrap180(animFrom0 + (animTo0 - animFrom0) * e);
      rotation[1] = animFrom1 + (animTo1 - animFrom1) * e;
      if (t >= 1) animating = false;
      return true;
    }
    if (coasting) {
      rotation[0] = wrap180(rotation[0] + velYaw * dt);
      const pitch = clampPitch(rotation[1] + velPitch * dt);
      if (pitch !== rotation[1] + velPitch * dt) velPitch = 0;
      rotation[1] = pitch;
      const decay = Math.exp(-dt / COAST_MS);
      velYaw *= decay;
      velPitch *= decay;
      if (Math.abs(velYaw) < 0.002 && Math.abs(velPitch) < 0.002) {
        coasting = false;
        lastInteraction = now;
      }
      return true;
    }
    // a hovered story, or a reader at the keys, holds the globe still
    if (!spinOn || reduced || dragging || hovered || keyboard) return false;
    const idle = lastInteraction + IDLE_MS;
    if (now < idle) {
      wake(idle);
      return false;
    }
    rotation[0] = wrap180(rotation[0] + SPIN * dt);
    return true;
  }

  /** How long a cycle's waves run, from the first leaving to the last fading out, ms. */
  const wavesMs = () => (waves - 1) * WAVE_GAP + waveMs;
  /** Some of the quake's reach is on the near side, though its point may be just over the limb. */
  const quakeSeen = (mark: Mark) => mark.depth > -Math.sin(reach * RAD);

  /** True while the selected mark's ring, or any of its quake's waves, is expanding; otherwise wakes for the next. */
  function pulse(now: number): boolean {
    if (reduced || !selected) return false;
    if (quake > 0 ? !quakeSeen(selected) : selected.depth <= 0) return false;
    const phase = (now - pulseFrom) % PULSE_EVERY;
    if (phase < (quake > 0 ? wavesMs() : PULSE_MS)) return true;
    wake(now + PULSE_EVERY - phase);
    return false;
  }

  // ---------- drawing ----------

  function project() {
    const yaw = rotation[0] * RAD;
    const pitch = rotation[1] * RAD;
    const cosP = Math.cos(pitch);
    const sinP = Math.sin(pitch);
    const cx = width / 2;
    const cy = height / 2;
    for (const mark of marks) {
      const lambda = mark.lambda + yaw;
      const x = Math.cos(lambda) * mark.cosPhi;
      const z = mark.sinPhi;
      mark.depth = x * cosP - z * sinP;
      mark.x = cx + radius * Math.sin(lambda) * mark.cosPhi;
      mark.y = cy - radius * (z * cosP + x * sinP);
    }
  }

  /** 1 inside, falling to 0 at the limb, where marks turn away. */
  const limb = (mark: Mark) => (mark.depth < LIMB ? Math.max(0, mark.depth) / LIMB : 1);
  /** The drawn radius: a mark turning away at the limb shrinks a little. */
  const drawn = (mark: Mark) => mark.r * (0.7 + 0.3 * limb(mark));

  function drawMark(mark: Mark) {
    const p = palette;
    const r = drawn(mark);
    ctx.globalAlpha = limb(mark);
    ctx.beginPath();
    ctx.arc(mark.x, mark.y, r + 1, 0, TAU);
    ctx.fillStyle = p.surface;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(mark.x, mark.y, r, 0, TAU);
    ctx.fillStyle = p.tones[mark.tone];
    ctx.fill();
  }

  /** A ring `gap` px outside the mark. */
  function ring(mark: Mark, gap: number, lineWidth: number, colour: string, alpha: number) {
    ctx.globalAlpha = alpha * limb(mark);
    ctx.beginPath();
    ctx.arc(mark.x, mark.y, drawn(mark) + gap, 0, TAU);
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = colour;
    ctx.stroke();
  }

  /** One wave: a circle on the sphere `degrees` from the quake's centre, cut off where it runs over the limb. */
  function wave(degrees: number, lineWidth: number, alpha: number) {
    const rim = quakeCircle.radius(degrees)().coordinates[0];
    if (!rim) return;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    // a line, not the polygon: a clipped polygon would be closed along the limb
    path({ type: "LineString", coordinates: rim });
    ctx.stroke();
  }

  /** The selected mark's quake: its felt area, and waves running out from it over the surface. */
  function drawQuake(mark: Mark, now: number) {
    const colour = palette.tones[mark.tone];
    quakeCircle.center([mark.point.lon, mark.point.lat]);
    ctx.globalAlpha = 0.08;
    ctx.beginPath();
    path(quakeCircle.radius(reach * FELT)());
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.strokeStyle = colour;
    if (reduced) {
      wave(reach * 0.5, 1, 0.35);
      return;
    }
    const since = now - pulseFrom;
    const phase = since % PULSE_EVERY;
    // this cycle's waves, and a strong quake's last ones still running from the cycle before
    const run = wavesMs();
    for (let start = phase; start <= since && start < run; start += PULSE_EVERY) {
      for (let i = 0; i < waves; i++) {
        const t = (start - i * WAVE_GAP) / waveMs;
        if (t < 0 || t >= 1) continue;
        const alpha = (i === 0 ? 0.85 : 0.7) * (1 - t);
        wave(WAVE_FROM + (reach - WAVE_FROM) * easeOut(t), 1.5 - t, alpha);
      }
    }
  }

  function draw(now: number) {
    if (paletteStale) {
      palette = readPalette(wrap);
      paletteStale = false;
    }
    const p = palette;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);
    if (radius <= 0) return;
    projection.rotate(rotation);
    ctx.globalAlpha = 1;
    ctx.lineJoin = "round";

    // the sea: the page itself
    ctx.beginPath();
    path(SPHERE);
    ctx.fillStyle = p.surface;
    ctx.fill();

    ctx.beginPath();
    path(grid);
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = p.line;
    ctx.stroke();

    if (atlas) {
      ctx.beginPath();
      path(atlas.land);
      ctx.fillStyle = p.fill;
      ctx.fill();

      ctx.strokeStyle = p.ink2;
      ctx.beginPath();
      path(atlas.borders);
      ctx.lineWidth = 0.5;
      ctx.globalAlpha = p.dark ? 0.2 : 0.22;
      ctx.stroke();

      ctx.beginPath();
      path(atlas.coast);
      ctx.lineWidth = 0.6;
      ctx.globalAlpha = p.dark ? 0.45 : 0.55;
      ctx.stroke();
    }

    // night: ink deepens it on paper; on the night edition the lit side is lifted instead
    ctx.beginPath();
    path(p.dark ? day : night);
    ctx.fillStyle = p.ink;
    ctx.globalAlpha = p.dark ? 0.05 : 0.06;
    ctx.fill();
    ctx.globalAlpha = 1;

    // the selected point's quake, over the land and under the marks
    project();
    if (quake > 0 && selected && quakeSeen(selected)) {
      drawQuake(selected, now);
      ctx.globalAlpha = 1;
    }

    ctx.beginPath();
    path(SPHERE);
    ctx.lineWidth = 1;
    ctx.strokeStyle = p.lineStrong;
    ctx.stroke();

    // marks, lightest first, the selected one on top
    for (const mark of marks) if (mark.depth > 0 && mark !== selected) drawMark(mark);
    const focusMark = hovered ?? tapped ?? (keyboard ? centreMark : null);
    if (focusMark && focusMark !== selected && focusMark.depth > 0) ring(focusMark, 3, 1, p.ink, 0.85);
    if (selected && selected.depth > 0) {
      drawMark(selected);
      ring(selected, 4, 2, p.ink, 1);
      // a quake sends out its waves instead
      if (!reduced && quake === 0) {
        const phase = (now - pulseFrom) % PULSE_EVERY;
        if (phase < PULSE_MS) {
          const t = phase / PULSE_MS;
          ring(selected, 4 + 18 * easeOut(t), 1, p.ink, 0.5 * (1 - t));
        }
      }
    }
    ctx.globalAlpha = 1;

    if (keyboard) {
      // the focus ring, and the centre that Enter picks from
      const cx = width / 2;
      const cy = height / 2;
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 4, 0, TAU);
      ctx.lineWidth = 2;
      ctx.strokeStyle = p.focus;
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cx - 6, cy);
      ctx.lineTo(cx + 6, cy);
      ctx.moveTo(cx, cy - 6);
      ctx.lineTo(cx, cy + 6);
      ctx.lineWidth = 1;
      ctx.strokeStyle = p.ink;
      ctx.globalAlpha = 0.6;
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // hover follows the marks as they turn under a still pointer
    if (pointerInside && pointerKind === "mouse" && pointerId === null) {
      const before = hovered;
      setHovered(nearest(hoverX, hoverY, HOVER_PX));
      if (hovered !== before) again = true;
    }
    if (keyboard) {
      centreMark = nearestCentre();
      if (centreMark !== announced && !animating) {
        announced = centreMark;
        live.textContent = centreMark ? describe(centreMark.point) : "";
      }
    }
    placeTip();
  }

  // ---------- hit testing ----------

  function nearest(x: number, y: number, reach: number): Mark | null {
    let best: Mark | null = null;
    let bestD = Number.POSITIVE_INFINITY;
    for (const mark of marks) {
      if (mark.depth <= PICK_DEPTH) continue;
      const dx = mark.x - x;
      const dy = mark.y - y;
      const d = dx * dx + dy * dy;
      const within = Math.max(reach, mark.r + 3);
      if (d <= within * within && d <= bestD) {
        best = mark;
        bestD = d;
      }
    }
    return best;
  }

  function nearestCentre(): Mark | null {
    let best: Mark | null = null;
    for (const mark of marks) if (mark.depth > PICK_DEPTH && (!best || mark.depth > best.depth)) best = mark;
    return best;
  }

  function setHovered(mark: Mark | null) {
    if (mark === hovered) return;
    hovered = mark;
    if (!mark) lastInteraction = performance.now();
    setCursor();
  }

  function setCursor() {
    const next = dragging ? "grabbing" : hovered ? "pointer" : pointerInside && insideSphere(hoverX, hoverY) ? "grab" : "";
    if (next === cursor) return;
    cursor = next;
    if (next) canvas.dataset["cursor"] = next;
    else delete canvas.dataset["cursor"];
  }

  function insideSphere(x: number, y: number) {
    const dx = x - width / 2;
    const dy = y - height / 2;
    return dx * dx + dy * dy <= (radius + 6) * (radius + 6);
  }

  // ---------- the tooltip ----------

  function describe(point: GlobePoint) {
    return point.sub ? `${point.label}. ${point.sub}` : point.label;
  }

  function placeTip() {
    // a tapped story's note goes when it turns away
    if (tapped && tapped.depth <= PICK_DEPTH) tapped = null;
    const target = hovered ?? tapped ?? (keyboard ? centreMark : null);
    const show = target && target.depth > PICK_DEPTH ? target : null;
    if (show !== tipMark || tipStale) {
      tipMark = show;
      tipStale = false;
      if (!show) {
        tip.hidden = true;
        return;
      }
      tipLabel.textContent = show.point.label;
      tipSub.textContent = show.point.sub ?? "";
      tipSub.hidden = !show.point.sub;
      tip.dataset["tone"] = show.point.tone;
      tip.hidden = false;
      tipW = tip.offsetWidth;
      tipH = tip.offsetHeight;
    }
    if (!show) return;
    const gap = drawn(show) + 10;
    const maxX = Math.max(4, width - tipW - 4);
    const maxY = Math.max(4, height - tipH - 4);
    let x: number;
    let y: number;
    if (show.x + gap + tipW <= width - 4) {
      x = show.x + gap;
      y = show.y - tipH / 2;
    } else if (show.x - gap - tipW >= 4) {
      x = show.x - gap - tipW;
      y = show.y - tipH / 2;
    } else {
      // no room beside it: above, or below near the top
      x = show.x - tipW / 2;
      y = show.y - gap - tipH >= 4 ? show.y - gap - tipH : show.y + gap;
    }
    x = Math.round(Math.max(4, Math.min(maxX, x)));
    y = Math.round(Math.max(4, Math.min(maxY, y)));
    tip.style.transform = `translate(${x}px, ${y}px)`;
  }

  // ---------- turning ----------

  function turnTo(yaw: number, pitch: number, ms: number, ease: (t: number) => number) {
    coasting = false;
    const to1 = clampPitch(pitch);
    if (ms <= 0) {
      animating = false;
      rotation[0] = wrap180(yaw);
      rotation[1] = to1;
      invalidate();
      return;
    }
    animFrom0 = rotation[0];
    animFrom1 = rotation[1];
    animTo0 = rotation[0] + wrap180(yaw - rotation[0]);
    animTo1 = to1;
    animStart = performance.now();
    animMs = ms;
    animEase = ease;
    animating = true;
    invalidate();
  }

  function centreOn(mark: Mark) {
    const instant = reduced || performance.now() - born < 500;
    turnTo(-mark.point.lon, -mark.point.lat, instant ? 0 : CENTRE_MS, easeInOut);
  }

  function resolveSelection() {
    selected = selectedId ? (byId.get(selectedId) ?? null) : null;
    if (selected && centredId !== selectedId) {
      centredId = selectedId;
      pulseFrom = performance.now();
      centreOn(selected);
    }
  }

  // ---------- input ----------

  function local(event: PointerEvent): [number, number] {
    const box = canvas.getBoundingClientRect();
    return [event.clientX - box.left, event.clientY - box.top];
  }

  function onPointerDown(event: PointerEvent) {
    if (pointerId !== null || !event.isPrimary) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const [x, y] = local(event);
    // beside the globe the page is left alone, except for a mark drawn at its edge
    if (!insideSphere(x, y) && !nearest(x, y, event.pointerType === "touch" ? TAP_PX : HOVER_PX)) return;
    pointerId = event.pointerId;
    pointerKind = event.pointerType;
    lock = pointerKind === "touch" ? "pending" : "turn";
    startX = lastX = event.clientX;
    startY = lastY = event.clientY;
    lastMove = event.timeStamp;
    travel = 0;
    velYaw = velPitch = 0;
    coasting = false;
    animating = false;
    keyboard = false;
    lastInteraction = performance.now();
    if (pointerKind !== "touch") canvas.setPointerCapture(event.pointerId);
    invalidate();
  }

  function onPointerMove(event: PointerEvent) {
    if (event.pointerId === pointerId) {
      if (lock === "scroll") return;
      if (lock === "pending") {
        const ax = Math.abs(event.clientX - startX);
        const ay = Math.abs(event.clientY - startY);
        if (Math.max(ax, ay) < 6) return;
        // mostly vertical: the page scrolls and the globe stays out of it
        lock = ay > ax ? "scroll" : "turn";
        if (lock === "scroll") return;
      }
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      travel += Math.abs(dx) + Math.abs(dy);
      if (!dragging && travel < 3) return;
      if (!dragging) {
        dragging = true;
        hovered = null;
        tapped = null;
        setCursor();
      }
      animating = false;
      const perPx = 180 / (Math.PI * Math.max(radius, 40));
      const yaw = dx * perPx;
      const pitchBefore = rotation[1];
      rotation[0] = wrap180(rotation[0] + yaw);
      rotation[1] = clampPitch(rotation[1] - dy * perPx);
      const dt = Math.max(4, event.timeStamp - lastMove);
      lastMove = event.timeStamp;
      // smoothed velocity, degrees per ms, for the coast on release
      velYaw = clampVelocity(velYaw * 0.5 + (yaw / dt) * 0.5);
      velPitch = clampVelocity(velPitch * 0.5 + ((rotation[1] - pitchBefore) / dt) * 0.5);
      lastInteraction = performance.now();
      invalidate();
      return;
    }
    if (event.pointerType !== "mouse" || pointerId !== null) return;
    const [x, y] = local(event);
    pointerInside = true;
    hoverX = x;
    hoverY = y;
    if (insideSphere(x, y)) lastInteraction = performance.now();
    const before = hovered;
    setHovered(nearest(x, y, HOVER_PX));
    setCursor();
    if (hovered !== before) invalidate();
  }

  function endDrag(event: PointerEvent, cancelled: boolean) {
    if (event.pointerId !== pointerId) return;
    const kind = pointerKind;
    const wasDragging = dragging;
    pointerId = null;
    dragging = false;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    lastInteraction = performance.now();
    if (cancelled || lock === "scroll") {
      setCursor();
      invalidate();
      return;
    }
    if (!wasDragging) {
      const [x, y] = local(event);
      const mark = nearest(x, y, kind === "touch" ? TAP_PX : HOVER_PX);
      if (kind !== "mouse") tapped = mark;
      if (mark) parts.pick(mark.point.id);
    } else if (!reduced && event.timeStamp - lastMove < 80 && Math.hypot(velYaw, velPitch) > 0.01) {
      coasting = true;
    }
    if (kind === "mouse") {
      const [x, y] = local(event);
      hoverX = x;
      hoverY = y;
      pointerInside = x >= 0 && y >= 0 && x <= width && y <= height;
    }
    setCursor();
    invalidate();
  }

  const onPointerUp = (event: PointerEvent) => endDrag(event, false);
  const onPointerCancel = (event: PointerEvent) => endDrag(event, true);
  const onLostCapture = (event: PointerEvent) => {
    if (event.pointerId === pointerId) endDrag(event, false);
  };

  function onPointerLeave(event: PointerEvent) {
    if (event.pointerType !== "mouse" || pointerId !== null) return;
    pointerInside = false;
    setHovered(null);
    setCursor();
    invalidate();
  }

  function onKeyDown(event: KeyboardEvent) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const step = event.shiftKey ? 30 : 10;
    let dYaw = 0;
    let dPitch = 0;
    switch (event.key) {
      case "ArrowLeft":
        dYaw = step;
        break;
      case "ArrowRight":
        dYaw = -step;
        break;
      case "ArrowUp":
        dPitch = -step;
        break;
      case "ArrowDown":
        dPitch = step;
        break;
      case "Enter": {
        event.preventDefault();
        const mark = centreMark ?? nearestCentre();
        if (mark) parts.pick(mark.point.id);
        return;
      }
      default:
        return;
    }
    event.preventDefault();
    keyboard = true;
    tapped = null;
    lastInteraction = performance.now();
    const base0 = animating ? animTo0 : rotation[0];
    const base1 = animating ? animTo1 : rotation[1];
    turnTo(base0 + dYaw, base1 + dPitch, reduced ? 0 : KEY_MS, easeOut);
  }

  function onFocus() {
    let visible = true;
    try {
      visible = canvas.matches(":focus-visible");
    } catch {
      // an older engine: treat focus as visible
    }
    keyboard = visible;
    announced = null;
    invalidate();
  }

  function onBlur() {
    keyboard = false;
    centreMark = null;
    invalidate();
  }

  // ---------- size, theme, visibility ----------

  function resize(w: number, h: number) {
    const nextDpr = Math.min(2, window.devicePixelRatio || 1);
    if (w === width && h === height && nextDpr === dpr) return;
    width = w;
    height = h;
    dpr = nextDpr;
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
    radius = Math.max(0, Math.min(w, h) / 2 - PAD);
    translate[0] = w / 2;
    translate[1] = h / 2;
    projection.scale(radius).translate(translate);
    tipStale = true;
    // resizing clears the canvas: draw now rather than show a blank frame
    if (width > 0) draw(performance.now());
    invalidate();
  }

  const resizeObserver = new ResizeObserver((entries) => {
    const box = entries[0]?.contentRect;
    if (box) resize(box.width, box.height);
  });
  resizeObserver.observe(wrap);

  const viewObserver = new IntersectionObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;
    inView = entry.isIntersecting;
    if (inView) invalidate();
    else stop();
  });
  viewObserver.observe(wrap);

  function retheme() {
    paletteStale = true;
    tipStale = true;
    invalidate();
  }
  const themeObserver = new MutationObserver(retheme);
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
  schemeQuery.addEventListener("change", retheme);

  function onMotion() {
    reduced = motionQuery.matches;
    if (reduced) coasting = false;
    invalidate();
  }
  motionQuery.addEventListener("change", onMotion);

  function onVisibility() {
    pageVisible = !document.hidden;
    if (!pageVisible) return stop();
    sunNow();
    invalidate();
  }
  document.addEventListener("visibilitychange", onVisibility);

  const sunTimer = window.setInterval(() => {
    sunNow();
    invalidate();
  }, 60_000);

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerCancel);
  canvas.addEventListener("lostpointercapture", onLostCapture);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("keydown", onKeyDown);
  canvas.addEventListener("focus", onFocus);
  canvas.addEventListener("blur", onBlur);

  if (!atlas) {
    loadAtlas().then(
      () => invalidate(),
      (error: unknown) => console.error("globe: the atlas did not load", error),
    );
  }

  return {
    setPoints(points) {
      const next: Mark[] = [];
      for (const point of points) {
        if (!Number.isFinite(point.lat) || !Number.isFinite(point.lon)) continue;
        const weight = Number.isFinite(point.weight) ? Math.max(0, Math.min(1, point.weight)) : 0;
        const phi = point.lat * RAD;
        next.push({
          point,
          weight,
          lambda: point.lon * RAD,
          cosPhi: Math.cos(phi),
          sinPhi: Math.sin(phi),
          r: R_MIN + (R_MAX - R_MIN) * Math.sqrt(weight),
          tone: TONE[point.tone] ?? TONE.news,
          x: 0,
          y: 0,
          depth: -1,
        });
      }
      // heavier marks are drawn last, over the lighter ones
      next.sort((a, b) => a.weight - b.weight);
      marks = next;
      byId = new Map(next.map((mark) => [mark.point.id, mark]));
      const again = (mark: Mark | null) => (mark ? (byId.get(mark.point.id) ?? null) : null);
      hovered = again(hovered);
      tapped = again(tapped);
      centreMark = again(centreMark);
      announced = again(announced);
      tipStale = true;
      if (width > 0) project();
      resolveSelection();
      invalidate();
    },
    setSelected(id) {
      selectedId = id;
      if (!id) {
        selected = null;
        centredId = null;
      } else resolveSelection();
      invalidate();
    },
    setSpin(on) {
      spinOn = on;
      invalidate();
    },
    setQuake(q) {
      const next = Number.isFinite(q) ? Math.max(0, Math.min(1, q)) : 0;
      if (next === quake) return;
      quake = next;
      waves = 1 + Math.round((WAVES_MAX - 1) * next);
      reach = REACH_MIN + (REACH_MAX - REACH_MIN) * next;
      waveMs = WAVE_MS_MIN + (WAVE_MS_MAX - WAVE_MS_MIN) * next;
      // the waves start again from the point
      pulseFrom = performance.now();
      invalidate();
    },
    retheme,
    destroy() {
      destroyed = true;
      stop();
      window.clearInterval(sunTimer);
      resizeObserver.disconnect();
      viewObserver.disconnect();
      themeObserver.disconnect();
      schemeQuery.removeEventListener("change", retheme);
      motionQuery.removeEventListener("change", onMotion);
      document.removeEventListener("visibilitychange", onVisibility);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("lostpointercapture", onLostCapture);
      canvas.removeEventListener("pointerleave", onPointerLeave);
      canvas.removeEventListener("keydown", onKeyDown);
      canvas.removeEventListener("focus", onFocus);
      canvas.removeEventListener("blur", onBlur);
    },
  };
}

// ---------- the component ----------

export function Globe(props: GlobeProps) {
  const { points, selected = null, onPick, spin = false, quake = 0, height = 360, className } = props;
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const tipLabelRef = useRef<HTMLDivElement>(null);
  const tipSubRef = useRef<HTMLDivElement>(null);
  const liveRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Engine | null>(null);
  const pickRef = useRef(onPick);
  const theme = usePref("theme");

  useEffect(() => {
    pickRef.current = onPick;
  }, [onPick]);

  useEffect(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    const tip = tipRef.current;
    const tipLabel = tipLabelRef.current;
    const tipSub = tipSubRef.current;
    const live = liveRef.current;
    if (!wrap || !canvas || !tip || !tipLabel || !tipSub || !live) return;
    const engine = createEngine({ wrap, canvas, tip, tipLabel, tipSub, live, pick: (id) => pickRef.current?.(id) });
    engineRef.current = engine;
    return () => {
      engine?.destroy();
      engineRef.current = null;
    };
  }, []);

  useEffect(() => {
    engineRef.current?.setPoints(points);
  }, [points]);

  useEffect(() => {
    engineRef.current?.setSelected(selected);
  }, [selected]);

  useEffect(() => {
    engineRef.current?.setSpin(spin);
  }, [spin]);

  useEffect(() => {
    engineRef.current?.setQuake(quake);
  }, [quake]);

  // the theme as chosen here; the system's own changes and `data-theme` are watched by the engine
  useEffect(() => {
    engineRef.current?.retheme();
  }, [theme]);

  const count = points.length;
  const label =
    count === 0
      ? "Globe: no stories placed yet."
      : `Globe: ${count} ${count === 1 ? "story" : "stories"} placed where they happened. Drag or use the arrow keys to turn it; Enter opens the story nearest the centre.`;

  return (
    <div
      ref={wrapRef}
      className={className ? `gl-globe ${className}` : "gl-globe"}
      style={{ height: typeof height === "number" ? `${height}px` : height }}
    >
      <canvas ref={canvasRef} className="gl-canvas" tabIndex={0} role="img" aria-label={label} />
      <div ref={tipRef} className="gl-tip" hidden aria-hidden="true">
        <div ref={tipLabelRef} className="gl-tip-label" />
        <div ref={tipSubRef} className="gl-tip-sub" />
      </div>
      <div ref={liveRef} className="gl-sr" aria-live="polite" />
    </div>
  );
}
